import { z } from "zod";

export const featureKinds = [
  "point",
  "structure",
  "gate",
  "stop",
  "arrow",
  "label",
  "approved-road",
  "wrong-road",
  "closed-road",
  "line",
  "area",
  "freehand",
  "reference-line",
] as const;
export const locales = ["en", "es", "fr"] as const;
export type Locale = (typeof locales)[number];
export const coordinateSchema = z.tuple([
  z.number().finite().min(-180).max(180),
  z.number().finite().min(-85).max(85),
]);
export type Coordinate = z.infer<typeof coordinateSchema>;
const line = z.array(coordinateSchema).min(2).max(15000);
const geometrySchema = z
  .discriminatedUnion("type", [
    z.object({ type: z.literal("Point"), coordinates: coordinateSchema }),
    z.object({ type: z.literal("LineString"), coordinates: line }),
    z.object({
      type: z.literal("Polygon"),
      coordinates: z
        .array(z.array(coordinateSchema).min(4).max(15000))
        .min(1)
        .max(20),
    }),
  ])
  .superRefine((g, ctx) => {
    if (g.type === "Polygon")
      for (const ring of g.coordinates) {
        if (ring[0][0] !== ring.at(-1)![0] || ring[0][1] !== ring.at(-1)![1])
          ctx.addIssue({
            code: "custom",
            message: "Close each area before saving.",
          });
      }
  });
const shortText = z.string().trim().max(160);
export const featureSchema = z
  .object({
    id: z.string().min(1).max(100),
    kind: z.enum(featureKinds),
    geometry: geometrySchema,
    label: shortText.default(""),
    translations: z
      .object({
        en: shortText.optional(),
        es: shortText.optional(),
        fr: shortText.optional(),
      })
      .default({}),
    visibility: z.enum(["public", "private"]).default("public"),
    color: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .default("#23864a"),
    width: z.number().min(1).max(24).default(6),
    opacity: z.number().min(0.15).max(1).default(0.85),
    labelOffset: z
      .tuple([z.number().min(-200).max(200), z.number().min(-200).max(200)])
      .default([12, -12]),
    rotation: z.number().min(0).max(359).default(0),
    publicNote: z.string().max(4000).default(""),
    privateNote: z.string().max(8000).default(""),
    source: z.string().max(2000).default(""),
    reviewState: z
      .enum(["unreviewed", "checked", "needs-review"])
      .default("unreviewed"),
    warning: z
      .enum(["none", "do-not-cross", "no-access", "bad-road"])
      .default("none"),
    attachmentIds: z.array(z.string().uuid()).max(8).default([]),
  })
  .superRefine((f, ctx) => {
    const pointKinds = ["point", "structure", "gate", "stop", "arrow", "label"];
    if (pointKinds.includes(f.kind) && f.geometry.type !== "Point")
      ctx.addIssue({
        code: "custom",
        message: "This marking needs a single point.",
      });
    if (f.kind === "area" && f.geometry.type !== "Polygon")
      ctx.addIssue({
        code: "custom",
        message: "An area needs a closed shape.",
      });
    if (
      !pointKinds.includes(f.kind) &&
      f.kind !== "area" &&
      f.geometry.type !== "LineString"
    )
      ctx.addIssue({ code: "custom", message: "This marking needs a line." });
    if (f.kind === "reference-line" && f.visibility !== "private")
      ctx.addIssue({
        code: "custom",
        message: "Reference lines must stay private.",
      });
  });
export type MapFeature = z.infer<typeof featureSchema>;
export const mapDocumentSchema = z
  .object({
    schemaVersion: z.literal(1).default(1),
    title: z.string().trim().min(1).max(120),
    center: coordinateSchema.default([-104.99, 39.74]),
    zoom: z.number().min(2).max(21).default(16),
    basemap: z.enum(["streets", "aerial", "terrain"]).default("aerial"),
    isExample: z.boolean().default(false),
    publicationProfile: z.enum(["general", "51time-driver"]).default("general"),
    features: z.array(featureSchema).max(4000).default([]),
    starterLabels: z.array(shortText).max(100).default([]),
    savedViews: z
      .array(
        z.object({
          id: z.string().max(100),
          name: shortText,
          center: coordinateSchema,
          zoom: z.number().min(2).max(21),
        }),
      )
      .max(100)
      .default([]),
  })
  .superRefine((doc, ctx) => {
    const ids = new Set<string>();
    let vertices = 0;
    for (const f of doc.features) {
      if (ids.has(f.id))
        ctx.addIssue({
          code: "custom",
          message: "Each marking must have a unique ID.",
        });
      ids.add(f.id);
      vertices +=
        f.geometry.type === "Point"
          ? 1
          : f.geometry.type === "LineString"
            ? f.geometry.coordinates.length
            : f.geometry.coordinates.flat().length;
    }
    if (vertices > 150000)
      ctx.addIssue({
        code: "custom",
        message: "Split this drawing into smaller maps.",
      });
  });
export type MapDocument = z.infer<typeof mapDocumentSchema>;
export const publicFeatureSchema = z.object({
  id: z.string(),
  kind: z.enum(featureKinds),
  geometry: geometrySchema,
  label: z.string(),
  translations: z.object({
    en: z.string().optional(),
    es: z.string().optional(),
    fr: z.string().optional(),
  }),
  color: z.string(),
  width: z.number(),
  opacity: z.number(),
  labelOffset: z.tuple([z.number(), z.number()]),
  rotation: z.number(),
  publicNote: z.string(),
  warning: z.enum(["none", "do-not-cross", "no-access", "bad-road"]),
  attachmentIds: z.array(z.string()),
});
export type PublishedFeature = z.infer<typeof publicFeatureSchema>;
export interface PublishedMap {
  schemaVersion: 1;
  title: string;
  center: Coordinate;
  zoom: number;
  basemap: MapDocument["basemap"];
  isExample: boolean;
  features: PublishedFeature[];
  savedViews: MapDocument["savedViews"];
}
export class PublicationError extends Error {}
const driverKinds = new Set([
  "structure",
  "gate",
  "stop",
  "arrow",
  "label",
  "approved-road",
  "wrong-road",
  "closed-road",
]);

/** Explicit field allowlist: adding a private field never makes it public accidentally. */
export function publishDocument(input: MapDocument): PublishedMap {
  const doc = mapDocumentSchema.parse(input);
  const visible = doc.features.filter((f) => f.visibility === "public");
  if (
    doc.publicationProfile === "51time-driver" &&
    visible.some((f) => !driverKinds.has(f.kind))
  )
    throw new PublicationError(
      "The 51time driver view only permits structure numbers, entrances, restrictions, arrows, labels and haul roads. Keep reference geometry private.",
    );
  return {
    schemaVersion: 1,
    title: doc.title,
    center: doc.center,
    zoom: doc.zoom,
    basemap: doc.basemap,
    isExample: doc.isExample,
    savedViews: doc.savedViews.map((v) => ({
      id: v.id,
      name: v.name,
      center: v.center,
      zoom: v.zoom,
    })),
    features: visible.map((f) => ({
      id: f.id,
      kind: f.kind,
      geometry: f.geometry,
      label: f.label,
      translations: f.translations,
      color: f.color,
      width: f.width,
      opacity: f.opacity,
      labelOffset: f.labelOffset,
      rotation: f.rotation,
      publicNote: f.publicNote,
      warning: f.warning,
      attachmentIds: f.attachmentIds,
    })),
  };
}

export const submissionSchema = z
  .object({
    clientId: z.string().uuid(),
    mapId: z.string().uuid(),
    basePublication: z.number().int().nonnegative(),
    note: z.string().trim().min(1).max(8000),
    features: z.array(featureSchema).max(100).default([]),
    attachmentIds: z.array(z.string().uuid()).max(8).default([]),
  })
  .superRefine((s, ctx) => {
    if (
      s.features.some(
        (f) =>
          !["point", "arrow", "line", "area", "freehand", "label"].includes(
            f.kind,
          ),
      )
    )
      ctx.addIssue({
        code: "custom",
        message: "Field feedback supports points, drawings, arrows and labels.",
      });
  });
export type SubmissionInput = z.infer<typeof submissionSchema>;

export const warnings = {
  "do-not-cross": {
    en: "DO NOT CROSS",
    es: "NO CRUZAR",
    fr: "NE PAS FRANCHIR",
  },
  "no-access": {
    en: "NO ACCESS",
    es: "ACCESO PROHIBIDO",
    fr: "ACCÈS INTERDIT",
  },
  "bad-road": {
    en: "DO NOT USE THIS ROAD",
    es: "NO USE ESTA VÍA",
    fr: "NE PAS EMPRUNTER CETTE ROUTE",
  },
} as const;

export type Conflict = {
  path: string;
  label: string;
  base: unknown;
  local: unknown;
  remote: unknown;
};
/** Three-way draft merge; conflicts retain local work and require an explicit choice before save. */
export function mergeDrafts(
  base: MapDocument,
  local: MapDocument,
  remote: MapDocument,
) {
  const conflicts: Conflict[] = [];
  const same = (a: unknown, b: unknown) =>
    JSON.stringify(a) === JSON.stringify(b);
  const choose = (
    path: string,
    label: string,
    b: unknown,
    l: unknown,
    r: unknown,
  ) => {
    if (same(l, b)) return r;
    if (same(r, b) || same(l, r)) return l;
    conflicts.push({ path, label, base: b, local: l, remote: r });
    return l;
  };
  const result = { ...remote } as MapDocument;
  for (const key of [
    "title",
    "center",
    "zoom",
    "basemap",
    "isExample",
    "publicationProfile",
    "savedViews",
    "starterLabels",
  ] as const) {
    (result as Record<string, unknown>)[key] = choose(
      key,
      key,
      base[key],
      local[key],
      remote[key],
    );
  }
  const b = new Map(base.features.map((f) => [f.id, f])),
    l = new Map(local.features.map((f) => [f.id, f])),
    r = new Map(remote.features.map((f) => [f.id, f]));
  result.features = [];
  for (const id of new Set([...r.keys(), ...l.keys(), ...b.keys()])) {
    const selected = choose(
      `features.${id}`,
      l.get(id)?.label || r.get(id)?.label || "Marking",
      b.get(id),
      l.get(id),
      r.get(id),
    );
    if (selected) result.features.push(selected as MapFeature);
  }
  return { document: result, conflicts };
}

/** Reopening a saved draft must not silently clear a conflict still awaiting a decision. */
export function recoverDraft(
  saved: { base: MapDocument; document: MapDocument; conflicts?: Conflict[] },
  remote: MapDocument,
) {
  const merged = mergeDrafts(saved.base, saved.document, remote);
  const valueAt = (doc: MapDocument, path: string): unknown =>
    path.startsWith("features.")
      ? doc.features.find((f) => f.id === path.slice(9))
      : doc[path as keyof MapDocument];
  for (const pending of saved.conflicts ?? []) {
    if (merged.conflicts.some((c) => c.path === pending.path)) continue;
    const local = valueAt(merged.document, pending.path);
    const current = valueAt(remote, pending.path);
    if (JSON.stringify(local) !== JSON.stringify(current))
      merged.conflicts.push({ ...pending, local, remote: current });
  }
  return merged;
}

export * from "./templates.ts";
